import { createFileRoute } from '@tanstack/react-router';
import { VStack, HStack, Text, Button, NumberTicker } from 'ui_2';
import { EventStatus, useEvent } from '@puzzlehq/sdk';
import { useGameStore } from '@src/data/useGameStore';
import { useNavigate } from '@tanstack/react-router';
import { Route as winnings_route } from '@routes/winnings';
import { useEffect, useMemo, useState } from 'react';
import { Header } from '@src/components/Header';
import { gamePhrase, MYSTERY_CITY_AUDIO_BASE_URL } from '@src/data/utils';
import {
  ArcadeStorePrompt,
  useArcadeStoreLink,
} from '@puzzlehq/frontend-utils';
import curvedTicket from '../../public/assets/curved_ticket.svg';
import useWindowDimensions from '@puzzlehq/frontend-utils/src/hooks/useWindowDimensions';
import { usePuzzleGameContext } from '@src/providers/puzzleProvider';

const winningsTryAgain = `${MYSTERY_CITY_AUDIO_BASE_URL}/winningsTryAgain.mp3`;
const winningsAlmost = `${MYSTERY_CITY_AUDIO_BASE_URL}/winningsAlmost.mp3`;
const winningsOk = `${MYSTERY_CITY_AUDIO_BASE_URL}/winningsOk.mp3`;
const winningsNiceTry = `${MYSTERY_CITY_AUDIO_BASE_URL}/winningsNiceTry.mp3`;
const winningsNiceWork = `${MYSTERY_CITY_AUDIO_BASE_URL}/winningsNiceWork.mp3`;
const winningsImpressive = `${MYSTERY_CITY_AUDIO_BASE_URL}/winningsImpressive.mp3`;
const winningsGenius = `${MYSTERY_CITY_AUDIO_BASE_URL}/winningsGenius.mp3`;

function isIOSWebView() {
  const userAgent = window.navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isSafari = /Safari/i.test(userAgent);
  const isWebView = !isSafari && isIOS;

  return isWebView;
}

export const Route = createFileRoute('/winnings')({
  component: RouteComponent,
  onLeave: () => {
    if (isIOSWebView()) {
      window.location.reload();
    }
  },
});

function RouteComponent() {
  const [distances, roundScores, score, submitEventId, clearGame, isMuted] =
    useGameStore((state) => [
      state.distances,
      state.roundScores,
      state.score,
      state.submitEventId,
      state.clearGame,
      state.isMuted,
    ]);
  const navigate = useNavigate({ from: winnings_route.id });
  const { event } = useEvent({ id: submitEventId ?? '' });

  const eventText = useMemo(() => {
    if (!event) return ' ';
    switch (event.status) {
      case EventStatus.Failed:
        return `Event has failed.${event.error ? ' ' + event.error : ''}`;
      default:
        return `PART will arrive in your wallet shortly.`;
    }
  }, [event?.status]);

  const winningsSounds = [
    winningsTryAgain,
    winningsAlmost,
    winningsOk,
    winningsNiceTry,
    winningsNiceWork,
    winningsImpressive,
    winningsGenius,
  ];

  const winningsSound = (score: number) => {
    if (score === 0) return winningsSounds[0];
    if (score < 3) return winningsSounds[1];
    if (score < 6) return winningsSounds[2];
    if (score < 9) return winningsSounds[3];
    if (score < 12) return winningsSounds[4];
    if (score < 15) return winningsSounds[5];
    if (score === 15) return winningsSounds[6];
  };

  useEffect(() => {
    if (isMuted) return;
    const audio = new Audio(winningsSound(score));
    audio.play();
  }, [score]);

  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const videoElement = document.getElementById(
      'winnings-video'
    ) as HTMLVideoElement | null;

    const handleCanPlay = () => {
      setVideoReady(true);
    };

    if (videoElement) {
      videoElement.addEventListener('canplay', handleCanPlay);
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('canplay', handleCanPlay);
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const videoElement = document.getElementById(
        'winnings-video'
      ) as HTMLVideoElement | null;
      if (document.visibilityState === 'visible' && videoElement) {
        videoElement.play();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const { parcBalance } = usePuzzleGameContext();
  const arcadeStoreLink = useArcadeStoreLink();

  const { width } = useWindowDimensions();
  const isMobile = width < 800;

  return (
    <div className='relative h-[100dvh] w-screen'>
      <div className='absolute inset-0 z-0'>
        <div className='relative'>
          <video
            id='winnings-video'
            playsInline
            autoPlay={true}
            loop={true}
            controls={false}
            muted={true}
            src={
              isMobile
                ? 'https://storage.googleapis.com/bucket-quickstart_jigsaw-bigshot/MysteryCity/videos/nofloat_mobile.mp4'
                : 'https://storage.googleapis.com/bucket-quickstart_jigsaw-bigshot/MysteryCity/videos/nofloat_desktop.mp4'
            }
            className='h-[100dvh] w-screen object-cover'
          />
          {!videoReady && (
            <img
              src={
                isMobile
                  ? 'https://storage.googleapis.com/bucket-quickstart_jigsaw-bigshot/MysteryCity/images/nofloat_mobile.png'
                  : 'https://storage.googleapis.com/bucket-quickstart_jigsaw-bigshot/MysteryCity/images/nofloat_desktop.png'
              }
              alt='Video placeholder'
              className='absolute top-0 h-[100dvh] w-screen object-cover'
            />
          )}
        </div>
      </div>
      <VStack className='relative z-10 h-[100dvh] flex-none'>
        <Header />
        <div className='flex h-full flex-col items-center'>
          <VStack className='flex-grow' />
          <VStack className='flex w-full max-w-[550px] items-center gap-4 p-4'>
            <Text family='space' color='aqua' font='bold-xl'>
              {gamePhrase(score)?.toUpperCase()}
            </Text>
            <HStack className='h-[100px] w-full items-center justify-between rounded-[20px] bg-black bg-gradient-to-b from-[rgba(96,186,225,0.2)] to-[rgba(96,186,225,0.2)] px-6 py-2'>
              {distances.map((distance, i) => (
                <VStack key={i} className='gap-1'>
                  <Text family='space' font='bold-sm' color='aqua'>
                    ROUND {i + 1}
                  </Text>
                  <Text family='space' font='sm' className='text-[#3CFF00]'>
                    {distance < 0
                      ? 'No guess'
                      : `${distance.toLocaleString()} km`}
                  </Text>
                  <HStack className='w-full items-center justify-center gap-1 rounded-full bg-[#345C6E] px-2 py-1'>
                    <img src={curvedTicket} height={8} width={12} />
                    <Text family='space' font='md' className='text-aqua'>
                      {roundScores[i]}
                    </Text>
                  </HStack>
                </VStack>
              ))}
            </HStack>
            <HStack className='flex h-[91px] w-full items-center justify-between rounded-[20px] bg-black bg-gradient-to-b from-[rgba(96,186,225,0.2)] to-[rgba(96,186,225,0.2)] px-6 py-2'>
              <Text family='space' font='lg' color='aqua'>
                TOTAL
                <br />
                WINNINGS
              </Text>
              <VStack className='items-center justify-center'>
                <HStack className='items-center gap-2'>
                  <img src={curvedTicket} height={17.5} width={32} />
                  <Text family='space' font='xl' className='text-aqua'>
                    <NumberTicker
                      value={score === 0 ? 1 : score}
                      className='text-aqua'
                    />
                  </Text>
                </HStack>
                {score === 0 && (
                  <Text family='space' font='sm' className='text-aqua'>
                    ticket for effort
                  </Text>
                )}
              </VStack>
            </HStack>
            <div className='min-h-5'>
              <Text
                family='space'
                font='md'
                className='bg-black text-center  text-aqua'
              >
                {eventText}
              </Text>
            </div>
            <Button
              variant='mysteryCityPrimary'
              className='w-full'
              onClick={() => {
                clearGame();
                navigate({ to: '/' });
              }}
            >
              {(parcBalance ?? 0) > 0 ? 'Play Again' : 'Go home'}
            </Button>
            <Button
              variant='primary'
              className='mb-4 w-full'
              onClick={() => window.open(arcadeStoreLink, '_blank')}
            >
              Go to Arcade Store
            </Button>
          </VStack>
        </div>
      </VStack>
      <ArcadeStorePrompt />
    </div>
  );
}
